import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import BackToTopButton from '../components/common/BackToTopButton';
import backgroundVideo from '../assets/videos/LandingPage-Excavator-01.mp4';
import landingBackgroundImg from '../assets/images/Landing.webp';

const FixedNavigation = styled.nav`
  position: fixed;
  top: 1rem;
  right: 2rem;
  display: flex;
  gap: 2rem;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  backdrop-filter: blur(5px);
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  position: relative;
  padding: 0.5rem 0;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 0;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const EducationSection = styled.section`
  padding: 4rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: 12px;
  backdrop-filter: blur(10px);
  text-align: center;
`;

const PageHeading = styled.h1`
  margin-bottom: 2rem;
  font-size: 3rem;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const PageDescription = styled.p`
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: #ffffff;
  text-align: center;
  line-height: 1.6;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const TableOfContents = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const ContentCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  text-align: center;
  transition: transform 0.2s ease;
  cursor: pointer;
  height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:hover {
    transform: translateY(-5px);
  }
`;

const ContentIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.primary};
`;

const ContentTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.primary};
`;

const ContentDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 1.5rem;
  flex-grow: 1;
`;

const ContentLink = styled(Link)`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primaryDark};
  }
`;

const VideoSection = styled.section`
  position: relative;
  height: 60vh;
  min-height: 500px;
  display: flex;
  align-items: center;
  overflow: hidden;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    min-height: 400px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 2;
  }
`;

const VideoBackground = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const VideoContent = styled.div`
  position: relative;
  z-index: 2;
  color: white;
  text-align: center;
  width: 100%;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    padding-bottom: 2rem;
  }
`;

const VideoHeading = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
`;

const VideoText = styled(motion.p)`
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto 2rem;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }
`;

const educationContent = [
  {
    icon: '🏗️',
    title: 'Construction',
    description: 'Explore construction management principles, techniques, and best practices.',
    link: '/education/construction'
  },
  {
    icon: '⚗️',
    title: 'Construction Materials',
    description: 'Explore the elements essential to construction with our comprehensive interactive periodic table.',
    link: '/education/periodic-table'
  },
  {
    icon: '🤖',
    title: 'Artificial Intelligence',
    description: 'Understanding AI concepts, machine learning, and their applications in construction.',
    link: '/education/artificial-intelligence'
  },
  {
    icon: '📊',
    title: 'Data Analytics',
    description: 'Learn about data analysis, visualization, and decision-making in construction projects.',
    link: '/education/data-analytics'
  },
  {
    icon: '📋',
    title: 'Project Documentation',
    description: 'Best practices for documentation, reporting, and communication in construction projects.',
    link: '/education/project-documentation'
  },
  {
    icon: '🔒',
    title: 'Safety & Compliance',
    description: 'Understanding safety protocols, regulations, and compliance requirements in construction.',
    link: '/education/safety-compliance'
  },
  {
    icon: '🔧',
    title: 'Hardware Development',
    description: 'Learn about hardware design, prototyping, and implementation for construction technology.',
    link: '/education/hardware-development'
  },
  {
    icon: '💻',
    title: 'Software Development',
    description: 'Explore programming fundamentals, web development, and software engineering principles.',
    link: '/education/software-development'
  }
];

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(${landingBackgroundImg}) no-repeat center center;
    background-size: cover;
    z-index: -2;
  }

  &::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: -1;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  z-index: 1;
`;

const ScrollToTopLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <NavLink to={to} onClick={handleClick}>
      {children}
    </NavLink>
  );
};

const EducationPage: React.FC = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.78;
    }
  }, []);

  const handleCardClick = (link: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <FixedNavigation>
          <ScrollToTopLink to="/">HOME</ScrollToTopLink>
          <ScrollToTopLink to="/about">ABOUT</ScrollToTopLink>
          <ScrollToTopLink to="/services">SERVICES</ScrollToTopLink>
          <ScrollToTopLink to="/contact">CONTACT</ScrollToTopLink>
        </FixedNavigation>
        <EducationSection>
          <PageHeading>Educational Resources</PageHeading>
          <PageDescription>
            Explore our comprehensive collection of educational resources designed to help you stay ahead in the ever-evolving construction industry.
          </PageDescription>
        </EducationSection>
        
        <VideoSection>
          <VideoBackground autoPlay loop muted playsInline>
            <source src={backgroundVideo} type="video/mp4" />
          </VideoBackground>
          <VideoContent>
            <VideoHeading
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Learn Through Interactive Content
            </VideoHeading>
            <VideoText
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Dive into our interactive learning materials and develop your understanding of foundational construction principles and skills.
            </VideoText>
          </VideoContent>
        </VideoSection>

        <TableOfContents>
          {educationContent.map((content, index) => (
            <motion.div 
              key={index} 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              whileHover={{ scale: 1.02 }}
            >
              <ContentLink 
                to={content.link} 
                style={{ textDecoration: 'none' }}
                onClick={() => handleCardClick(content.link)}
              >
                <ContentCard>
                  <ContentIcon>{content.icon}</ContentIcon>
                  <ContentTitle>{content.title}</ContentTitle>
                  <ContentDescription>{content.description}</ContentDescription>
                  <ContentLink to={content.link}>
                    Learn More →
                  </ContentLink>
                </ContentCard>
              </ContentLink>
            </motion.div>
          ))}
        </TableOfContents>
        <BackToTopButton isVisible={showBackToTop} onClick={scrollToTop} />
      </ContentWrapper>
    </PageContainer>
  );
};

export default EducationPage; 